/**
 * Time utilities for the sales-forecast feature.
 * Copied from packages/hcem/src/shared/selectors/time.ts
 * TODO: Extract to a shared @hconnect/common package
 */
import {
  dayNameFormatter,
  formatLocalizedDateMonth,
  formatLocalizedDateWithShortYear,
  shortDayNameFormatter,
  shortMonthNameFormatter,
  timeFormatter,
  dayFormatter
} from '@hconnect/uikit/src/common/utils/dateformatting'
import moment, {Moment} from 'moment-timezone'

export type Range<T> = [start: T, end: T]
export type MomentRange = Range<Moment>

// Takes a date and returns YYYY-MM-DD
export const convertToDate = (dateTime: Moment): string => dateTime.format('YYYY-MM-DD')

interface GetListOfDaysParams {
  start: Moment
  end?: Moment
  additionalDays?: number
}

export const getListOfDays = ({start, end, additionalDays}: GetListOfDaysParams): Moment[] => {
  const retList: Moment[] = []
  retList.push(start)

  if (end !== undefined) {
    const current: Moment = start.clone().add(1, 'day')
    while (current < end) {
      retList.push(current.clone())
      current.add(1, 'days')
    }
  }

  if (additionalDays && additionalDays > 0) {
    const lastInList: Moment = retList[retList.length - 1]
    for (let i = 1; i <= additionalDays; i++) {
      retList.push(lastInList.clone().add(i, 'days'))
    }
  }
  return retList
}

/**
 * Returns localized short month name and day of month, example: "MMM DD"
 */
export const getLocalizedShortMonthDayFormat = (datetime: Moment, locale: string): string => {
  const utcOffset = datetime.utcOffset()
  const shortMonth = shortMonthNameFormatter(datetime.toDate(), locale, utcOffset)
  const day = dayFormatter(datetime.toDate(), locale, utcOffset)
  return `${shortMonth} ${day}`
}

/**
 * Returns localized date time, example: "ddd DD/MM, HH:mm"
 */
export const getLocalizedDayMonthTimeFormat = (
  datetime: Moment,
  timezoneId: string,
  locale: string
): string => {
  const timezoneOffset = datetime.tz(timezoneId).utcOffset()
  const shortDayName = shortDayNameFormatter(datetime, locale, timezoneOffset)
  const dateMonth = formatLocalizedDateMonth(datetime, locale)
  const time = timeFormatter(datetime, locale, timezoneOffset)
  return `${shortDayName} ${dateMonth}, ${time}`
}

export const longDayNameAndDateFormatter = (
  datetime: Moment,
  locale: string,
  options?: {hideYear: boolean}
): string => {
  const day = dayNameFormatter(datetime, locale, datetime.utcOffset())
  const shouldHideYear = options?.hideYear ?? false
  const formatDateFn = shouldHideYear ? formatLocalizedDateMonth : formatLocalizedDateWithShortYear
  const date = formatDateFn(datetime, locale)
  return `${day}, ${date}`
}

export const longDayNameAndDateRangeFormatter = (
  [start, end]: MomentRange,
  locale: string,
  timezoneId: string
): string => {
  const isSameYear = start.tz(timezoneId).isSame(end.tz(timezoneId), 'year')
  const startFormatted = longDayNameAndDateFormatter(start.tz(timezoneId), locale, {
    hideYear: isSameYear
  })
  const endFormatted = longDayNameAndDateFormatter(end.tz(timezoneId), locale)
  return `${startFormatted} - ${endFormatted}`
}
